import { useQuery } from '@tanstack/react-query'
import Image from 'next/image'
import apis from '../../../../modules/apis'
import { axiosGet } from '../../../../modules/axiosHelper'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { isB2BHRGroup } from '../../../../modules/utils'

export enum PromotionCode {
  Secret = 111,
  BlackFriday = 112,
  SecretEncore = 119,
}

const promotionCode = async () => {
  const response = await axiosGet(apis.promotion.code())
  return response.promotion_id
}

export const usePromotionCode = (config = {}) => {
  return useQuery({
    queryKey: ['promotionCode'],
    queryFn: promotionCode,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
    ...config,
  })
}

const IndividualPricingHero = ({ isAiTutor = false }) => {
  const { locale } = useTranslation()
  const isMobile = useIsMobile()

  const imageLocale = locale === 'ko' || locale === 'en' ? locale : 'en'

  const imgData = isMobile
    ? individualHeroImages.mobile['purchasedUserNormal'][imageLocale]
    : individualHeroImages.desktop['purchasedUserNormal'][imageLocale]

  return (
    <>
      {!isMobile && (
        <div className="relative flex justify-center" style={{ backgroundColor: imgData?.bgColor }}>
          <div className="relative flex justify-center max-w-[1180px] cursor-pointer">
            {imgData && (
              <Image
                alt="pricing_desktop_hero_image"
                className=" cursor-pointer delay-[initial]"
                priority={true}
                src={imgData.url}
                width={1180}
                height={425}
                quality={100}
              />
            )}
          </div>
        </div>
      )}
      {isMobile && (
        <div className="relative w-full" style={{ backgroundColor: imgData?.bgColor }}>
          {imgData && (
            <Image
              alt="pricing_desktop_hero_image"
              className="mx-auto cursor-pointer delay-[initial]"
              priority={true}
              src={imgData.url}
              width={1180}
              height={424}
              quality={100}
            />
          )}
        </div>
      )}
    </>
  )
}

export const BusinessPricingHero = () => {
  const { locale } = useTranslation()
  const { currentUser } = useCurrentUser()
  const isMobile = useIsMobile()

  const isB2BHR = isB2BHRGroup(currentUser)
  const isUser = currentUser && !isB2BHR

  const imageLocale = locale === 'ko' || locale === 'en' ? locale : 'en'

  const imgData = isMobile
    ? businessHeroImages.mobile[isUser ? 'user' : isB2BHR ? 'b2b2hr' : 'guest'][imageLocale]
    : businessHeroImages.desktop[isUser ? 'user' : isB2BHR ? 'b2b2hr' : 'guest'][imageLocale]

  return (
    <>
      {!isMobile && (
        <div className="relative flex justify-center" style={{ backgroundColor: imgData?.bgColor }}>
          <div className="relative flex justify-center max-w-[1180px] cursor-pointer">
            {imgData && (
              <Image
                alt="pricing_desktop_hero_image"
                className=" cursor-pointer delay-[initial]"
                priority={true}
                src={imgData.url}
                width={1180}
                height={425}
                quality={100}
              />
            )}
          </div>
        </div>
      )}
      {isMobile && (
        <div className="relative w-full" style={{ backgroundColor: imgData?.bgColor }}>
          {imgData && (
            <Image
              alt="pricing_desktop_hero_image"
              className="mx-auto cursor-pointer delay-[initial]"
              priority={true}
              src={imgData.url}
              width={1180}
              height={424}
              quality={100}
            />
          )}
        </div>
      )}
    </>
  )
}

export default IndividualPricingHero

export const individualHeroImages = {
  desktop: {
    aiTutor: {
      ko: {
        url: '',
        bgColor: '#1A1A1A',
      },
      en: {
        url: '',
        bgColor: '#1A1A1A',
      },
    },
    noPurchasedUser: {
      ko: {
        url: '',
        bgColor: '#1A1A1A',
      },
      en: {
        url: '',
        bgColor: '#1A1A1A',
      },
    },
    purchasedUserNormal: {
      ko: {
        url: 'https://d38emex6h5e12i.cloudfront.net/2024-image/20241206_pricinghero_bf_login_original_web_kr_export.webp',
        bgColor: '#140F33',
      },
      en: {
        url: 'https://d38emex6h5e12i.cloudfront.net/2024-image/20241206_pricinghero_bf_login_original_web_eng_export.webp',
        bgColor: '#140F33',
      },
    },
  },
  mobile: {
    aiTutor: {
      ko: {
        url: '',
        bgColor: '#1A1A1A',
      },
      en: {
        url: '',
        bgColor: '#1A1A1A',
      },
    },
    noPurchasedUser: {
      ko: {
        url: '',
        bgColor: '#1A1A1A',
      },
      en: {
        url: '',
        bgColor: '#1A1A1A',
      },
    },
    purchasedUserNormal: {
      ko: {
        url: 'https://d38emex6h5e12i.cloudfront.net/2024-image/20241206_pricinghero_bf_login_original_mo_kr.webp',
        bgColor: '#140F33',
      },
      en: {
        url: 'https://d38emex6h5e12i.cloudfront.net/2024-image/20241206_pricinghero_bf_login_original_mo_eng.webp',
        bgColor: '#140F33',
      },
    },
  },
}

export const businessHeroImages = {
  desktop: {
    guest: {
      ko: {
        url: 'https://d38emex6h5e12i.cloudfront.net/2024-image/1731652009516_pricinghero_b2b2c_nonlogin_web_kr_export.webp',
        bgColor: '#D7D8FF',
      },
      en: {
        url: 'https://d38emex6h5e12i.cloudfront.net/2024-image/1731652048015_pricinghero_b2b2c_nonlogin_web_eng_export.webp',
        bgColor: '#D7D8FF',
      },
      ja: {
        url: 'https://d38emex6h5e12i.cloudfront.net/2024-image/1731652057531_pricinghero_b2b2c_nonlogin_web_jp_export.webp',
        bgColor: '#D7D8FF',
      },
    },
    user: {
      ko: {
        url: 'https://d38emex6h5e12i.cloudfront.net/2024-image/1732672577020_pricinghero_b2b2c_nonlogin_web_kr_export.webp',
        bgColor: '#473A9C',
      },
      en: {
        url: 'https://d38emex6h5e12i.cloudfront.net/2024-image/1732672586785_pricinghero_b2b2c_nonlogin_web_kr_export%20%281%29.webp',
        bgColor: '#473A9C',
      },
    },
    b2b2hr: {
      ko: {
        url: 'https://d38emex6h5e12i.cloudfront.net/2024-image/1731652108360_pricinghero_b2bhr_login_web_kr_export.webp',
        bgColor: '#5E46E1',
      },
      en: {
        url: 'https://d38emex6h5e12i.cloudfront.net/2024-image/1731652119589_pricinghero_b2bhr_login_web_eng_export.webp',
        bgColor: '#5E46E1',
      },
      ja: {
        url: 'https://d38emex6h5e12i.cloudfront.net/2024-image/1731652128924_pricinghero_b2bhr_login_web_jp_export.webp',
        bgColor: '#5E46E1',
      },
    },
  },
  mobile: {
    guest: {
      ko: {
        url: 'https://d38emex6h5e12i.cloudfront.net/2024-image/1731543942887_pricinghero_b2b2c_nonlogin_mo_kr.webp',
        bgColor: '#D7D8FF',
      },
      en: {
        url: 'https://d38emex6h5e12i.cloudfront.net/2024-image/1731543951818_pricinghero_b2b2c_nonlogin_mo_eng.webp',
        bgColor: '#D7D8FF',
      },
      ja: {
        url: 'https://d38emex6h5e12i.cloudfront.net/2024-image/1731543959020_pricinghero_b2b2c_nonlogin_mo_jp.webp',
        bgColor: '#D7D8FF',
      },
    },
    user: {
      ko: {
        url: 'https://d38emex6h5e12i.cloudfront.net/2024-image/1732697178893_pricinghero_b2b2c_nonlogin_mo_kr%20%282%29.webp',
        bgColor: '#473A9C',
      },
      en: {
        url: 'https://d38emex6h5e12i.cloudfront.net/2024-image/1732697208220_pricinghero_b2b2c_nonlogin_mo_kr%20%283%29.webp',
        bgColor: '#473A9C',
      },
    },
    b2b2hr: {
      kr: {
        url: 'https://d38emex6h5e12i.cloudfront.net/2024-image/1731544143469_pricinghero_b2bhr_login_mo_kr.webp',
        bgColor: '#5E46E1',
      },
      en: {
        url: 'https://d38emex6h5e12i.cloudfront.net/2024-image/1731544175059_pricinghero_b2bhr_login_mo_eng.webp',
        bgColor: '#5E46E1',
      },
      ja: {
        url: 'https://d38emex6h5e12i.cloudfront.net/2024-image/1731544183184_pricinghero_b2bhr_login_mo_jp.webp',
        bgColor: '#5E46E1',
      },
    },
  },
}
